import { useQuery } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { accountsApi } from '../api/client'
import type { Book } from '../types'
import './KillEntriesCard.css'

interface KillEntriesCardProps {
  book: Book
}

/**
 * Read-only display of the live book's kill-entries overlay flag. No
 * mutation control here — this is a status readout, not a control panel.
 */
export function KillEntriesCard({ book }: KillEntriesCardProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['overlay', book.id],
    queryFn: () => accountsApi.overlay(book.id),
    retry: 1,
    refetchInterval: 60000,
  })

  const entriesEnabled = data?.overlay?.conviction_entries_enabled
  const killed = entriesEnabled === false

  return (
    <div className="card kill-entries-card">
      <div className="card-header">
        <h3 className="card-title">
          <KeyRound size={18} className="icon" aria-hidden="true" />
          Kill-entries
        </h3>
      </div>

      {isLoading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon"><KeyRound size={28} aria-hidden="true" /></div>
          <div className="empty-text">overlay unavailable</div>
        </div>
      ) : (
        <>
          <div className={`kill-entries-badge ${killed ? 'kill-entries-badge--killed' : 'kill-entries-badge--enabled'}`}>
            <span className="kill-entries-badge-dot" aria-hidden="true" />
            {killed ? 'ENTRIES KILLED' : 'entries enabled'}
          </div>
          <p className="caption kill-entries-caption">
            kill-entries is restrict-only and takes effect next cycle; exits and
            mirror-exits keep running (a live pause that de-risks without abandoning
            positions).
          </p>
        </>
      )}
    </div>
  )
}
